import type { SdkPayments, SdkPaymentsInstance } from '@yuno-payments/sdk-web-types'

// Origins the Web SDK talks to on the checkout path, in production spelling
// (the package maps env 'prod' to an empty environment suffix):
//   sdk-web.y.uno — main.js plus every lazy chunk the SDK pulls after boot
//                   (seamless-checkout, apple-pay-controller, …). Fetched by a
//                   plain <script>, so no crossorigin: a preconnect carrying one
//                   warms the CORS pool the script will not use.
//   api.y.uno     — the REST calls behind Yuno.initialize (initConfig) and
//                   mountSeamlessCheckout (get_settings). Reached by fetch under
//                   CORS, so the preconnect must be anonymous or the browser
//                   opens a second connection anyway and the hint buys nothing.
// The card-form and 3DS micro-apps sit on further hosts (see Yuno's white-label
// proxy doc, which lists sdk-web-card.* and sdk-3ds.*), but their production
// hostnames are not confirmed anywhere we can check from here — a wrong host
// spends a connection slot for nothing, so they are left out rather than
// guessed. Confirm them from a checkout waterfall before adding.
export const YUNO_ORIGINS: { href: string; anonymous: boolean }[] = [
  { href: 'https://sdk-web.y.uno', anonymous: false },
  { href: 'https://api.y.uno', anonymous: true },
]

let yunoSdkPromise: Promise<SdkPayments> | null = null

// Yuno distributes its web SDK by CDN only, so the npm package is a thin loader:
// `loadScript()` injects the official <script> and resolves `window.SdkPayments`
// once it has executed. One call therefore downloads, runs and resolves the SDK
// — there is no second bundle to warm separately the way Primer's shim has.
//
// A rejected load is never cached: the singleton resets so the next call (the
// integration's online retry, the checkout mount, a reinit) re-attempts. This
// was already the right behaviour when the mount was the only caller, but the
// pre-warm makes it load-bearing — it moves the first load to the email step,
// where a visitor who is briefly offline is a real scenario, and a cached
// rejection would leave them with a permanently dead checkout that nothing
// short of a full page reload could recover.
export const loadYunoSdk = (): Promise<SdkPayments> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Yuno SDK requires a browser environment'))
  }
  if (!yunoSdkPromise) {
    yunoSdkPromise = import('@yuno-payments/sdk-web')
      .then((m) => m.loadScript())
      .catch((err) => {
        yunoSdkPromise = null
        throw err
      })
  }
  return yunoSdkPromise
}

// One SDK instance per page session, shared by every YunoCheckout mount.
//
// This exists to bound a leak inside the SDK. `Yuno.initialize()` builds a fresh YunoConfig that
// subscribes to the SDK's module-level event emitter (PAYMENT_SUBMIT_ONE_TIME_TOKEN_FORM,
// PAYMENT_START, PAYMENT_STATUS, ...), and the SDK exposes NO teardown for it — the only unmount
// methods on the instance are for external buttons and secure fields, there is no destroy(). So
// every initialize adds listeners that are never released.
//
// `yuno` used to be a closure variable on each useYuno instance, so navigating away and back created
// a new composable, saw a null instance, and initialized again: one more set of listeners per
// navigation, hence `MaxListenersExceededWarning` after ~11 round trips, and stale configs still
// subscribed to submit events. Holding it at module scope reduces that to one initialize per page
// session — or per genuinely-required re-initialize, below.
//
// Repeated `startSeamlessCheckout` on one instance is already how the existing reinit path works
// (reinitCheckout re-renders without re-initializing), so cross-mount reuse is the same operation.
let instanceKey = ''
let instanceSpent = false
let instancePromise: Promise<SdkPaymentsInstance> | null = null

// A spent or wrong-key instance is ABANDONED, not destroyed — the SDK gives us no way to release it,
// so its listeners stay. That is the residual leak, now bounded to one per payment attempt or key
// change instead of one per navigation.
export const getYunoInstance = (publicApiKey: string): Promise<SdkPaymentsInstance> => {
  if (instancePromise && !instanceSpent && instanceKey === publicApiKey) return instancePromise

  instanceKey = publicApiKey
  instanceSpent = false
  instancePromise = loadYunoSdk()
    .then((Yuno) => Yuno.initialize(publicApiKey))
    .catch((error) => {
      // Never cache a rejection: the next mount must retry rather than inherit a dead promise. Same
      // rule as loadYunoSdk, and load-bearing for the same reason — a briefly-offline visitor would
      // otherwise have a permanently dead checkout.
      if (instanceKey === publicApiKey) instancePromise = null
      throw error
    })

  return instancePromise
}

// A failed payment SPENDS the instance: the SDK's ApplePayController latches `isExecuted` and its
// handler then silently no-ops, and `PaymentRetry.isTriggeredByExternalButton` is never reset. See
// YUNO_CHECKOUT.md. Re-initializing is the only reset the SDK offers, so mark it here and the next
// getYunoInstance builds a fresh one. Must live with the instance rather than in useYuno: the flag
// has to survive the remount that follows onCheckoutFail, or the new mount would reuse the spent
// instance and reproduce the dead-Apple-Pay-button bug.
export const markYunoInstanceSpent = (): void => {
  instanceSpent = true
}
